import { FC, useMemo, useState } from 'react'
import cx from 'classnames'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { useTranslation } from 'next-i18next'
import * as RadioGroup from '@radix-ui/react-radio-group'

import { formatStandardListingProperties } from '../ListingsGrid'

import { Listing } from '../../services/getListings'
import { useTracking } from '../../services/context/TrackingContext'

import {
  CalculatorPageContent,
  DEFAULT_TERM_LENGTH,
  VALUE_SHARE_PERCENT,
} from '../../hooks/useAcreCalculator'

import { arrayFromRange } from 'utils'
import { abbreviateCurrency } from '../../utils/abbreviateCurrency'
import { calculateMonthlyMortgage, calculatorFns } from 'calculations'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

ChartJS.defaults.font.family = `"Suisse Intl", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`

const options = (
  stepSize: number,
  t: (key: string) => string
): ChartOptions<'line'> => ({
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
  },
  events: [],
  scales: {
    y: {
      title: {
        display: true,
        text: t('chart.y-axis'),
        font: {
          size: 10,
          weight: '700',
        },
      },
      ticks: {
        padding: 6,
        callback: (value: number | string) => {
          return abbreviateCurrency(Number(value))
        },
        stepSize,
      },
      grid: {
        drawTicks: false,
      },
    },
    x: {
      grid: {
        display: false,
      },
      ticks: {
        autoSkip: false,
        align: 'start',
        maxRotation: 0,
        font: {
          weight: '700',
        },
        callback: (value: number | string, index: number) => {
          if ([0, 12, 24].includes(index)) {
            return `${t('chart.year')} ${[0, 12, 24].indexOf(index) + 1}`
          }
          return ''
        },
        major: {
          enabled: true,
        },
      },
    },
  },
  elements: {
    point: {
      radius: 0,
    },
  },
})

const HpaOption: FC<{
  rate: number
  selectedHpa: number
}> = ({ rate, children, selectedHpa }) => {
  const isActive = rate === selectedHpa
  return (
    <RadioGroup.Item
      className={cx('p-10 hover:text-teal-3', {
        'text-teal-3': isActive,
      })}
      value={rate.toString()}
    >
      {children}
    </RadioGroup.Item>
  )
}

const Chart = ({
  calculator,
  listing,
}: {
  calculator: CalculatorPageContent
  listing: Listing
}) => {
  const { t } = useTranslation('homes')
  const { track } = useTracking()
  const [hpa, setHpa] = useState(calculator.chartHpaStandard)

  const {
    calculateMortgageTotalValue,
    calculateSaverMonthlyPayment,
    calculateBoostMonthlyPayment,
    calculateProductTotalValue,
    calculateExpectedClosingCosts,
    calculateMortgagePmi,
    calculatePropertyTaxes,
    calculateInsurance,
  } = useMemo(() => {
    return calculatorFns({
      ...calculator,
      hpa,
      propertyAnnualTaxAmount: listing.taxes.annualAmount,
    })
  }, [calculator, hpa, listing.taxes.annualAmount])

  const expectedClose = listing.estimatedSalePrice
  const valueShare = VALUE_SHARE_PERCENT * expectedClose
  const loanBalance = expectedClose - valueShare
  const expectedClosingCosts = calculateExpectedClosingCosts(expectedClose)
  const months = DEFAULT_TERM_LENGTH * 12
  const interestRate = calculator.interestRateAcre / 100
  const hoaFees = 0

  const monthlyPaymentMortgage = calculateMonthlyMortgage(
    loanBalance,
    interestRate
  )

  const pmiMortgage = calculateMortgagePmi(
    loanBalance,
    expectedClose,
    valueShare
  )
  const propertyTaxesMortgage =
    calculatePropertyTaxes(expectedClose, listing.market) * -1
  const insuranceMortgage = calculateInsurance(expectedClose, 1)
  const monthlyTotalMortgage =
    monthlyPaymentMortgage +
    pmiMortgage +
    propertyTaxesMortgage +
    insuranceMortgage +
    hoaFees

  const mortgageTotalValueOverTime = arrayFromRange({
    min: 0,
    max: months,
  }).map((month) =>
    calculateMortgageTotalValue(
      loanBalance,
      month,
      expectedClose,
      valueShare,
      interestRate
    )
  )

  const saverTotalValueOverTime = arrayFromRange({ min: 0, max: months }).map(
    (month) =>
      calculateProductTotalValue(
        'saver',
        loanBalance,
        valueShare,
        month,
        expectedClose,
        monthlyTotalMortgage,
        calculateSaverMonthlyPayment(monthlyTotalMortgage) + hoaFees,
        expectedClosingCosts,
        interestRate,
        undefined,
        listing.market
      )
  )
  const boostTotalValueOverTime = arrayFromRange({ min: 0, max: months }).map(
    (month) =>
      calculateProductTotalValue(
        'boost',
        loanBalance,
        valueShare,
        month,
        expectedClose,
        monthlyTotalMortgage,
        calculateBoostMonthlyPayment(monthlyTotalMortgage) + hoaFees,
        expectedClosingCosts,
        interestRate,
        undefined,
        listing.market
      )
  )

  let lowest = saverTotalValueOverTime[0]
  let highest = saverTotalValueOverTime[0]

  const allValues = [
    ...saverTotalValueOverTime,
    ...mortgageTotalValueOverTime,
    ...boostTotalValueOverTime,
  ]
  allValues.forEach((value) => {
    if (value < lowest) lowest = value
    if (value > highest) highest = value
  })

  const data: ChartData<'line'> = {
    labels: arrayFromRange({ min: 0, max: 36 }),
    datasets: [
      {
        label: 'Saver',
        data: saverTotalValueOverTime,
        borderColor: '#0B8381', // teal-3
        cubicInterpolationMode: 'monotone',
      },
      {
        label: 'Boost',
        data: boostTotalValueOverTime,
        borderColor: '#CB997E', // tan-3
        cubicInterpolationMode: 'monotone',
      },
      {
        label: 'Mortgage',
        data: mortgageTotalValueOverTime,
        borderColor: '#757575', // grey-6
        cubicInterpolationMode: 'monotone',
        borderDash: [4],
      },
    ],
  }

  const lowestRounded = Math.floor(lowest / 10000) * 10000
  const highestRounded = Math.ceil(highest / 10000) * 10000
  const stepSize =
    Math.ceil((highestRounded - lowestRounded) / 4 / 10000) * 10000

  const trackingTargets = {
    [calculator.chartHpaStandard]: 'Standard',
    [calculator.chartHpaHigh]: 'HPA High',
    [calculator.chartHpaDown]: 'HPA Down',
  }

  return (
    <div className="relative z-0">
      <h3 className="util-label">{t('chart.title')}</h3>
      <div className="mt-20 rounded-md border border-green-1 bg-white p-24 shadow-md">
        <p className="sr-only" id="hpa-label">
          Home Price Appreciation Rate
        </p>
        <RadioGroup.Root
          value={hpa.toString()}
          onValueChange={(rate: string) => {
            setHpa(Number(rate))
            track({
              event: 'Property Listing Scenarios Chart Adjusted',
              properties: {
                ...formatStandardListingProperties(listing),
                chart_scenario_target: trackingTargets[Number(rate)],
              },
            })
          }}
          className="relative z-0 grid grid-cols-3 rounded-sm border border-green-1 bg-grey-2 font-semibold util-caption"
          aria-labelledby="hpa-label"
        >
          <HpaOption selectedHpa={hpa} rate={calculator.chartHpaDown}>
            {t('chart.hpa-down')}
          </HpaOption>
          <HpaOption selectedHpa={hpa} rate={calculator.chartHpaStandard}>
            {t('chart.hpa-standard')}
          </HpaOption>
          <HpaOption selectedHpa={hpa} rate={calculator.chartHpaHigh}>
            {t('chart.hpa-high')}
          </HpaOption>
          <span
            className={cx(
              'absolute inset-0 -z-10 w-1/3 rounded-sm bg-white text-teal-3 transition-transform duration-300 [box-shadow:1px_0_14px_rgba(0,0,0,0.19)]',
              {
                'translate-x-0': hpa === calculator.chartHpaDown,
                'translate-x-full': hpa === calculator.chartHpaStandard,
                'translate-x-[200%]': hpa === calculator.chartHpaHigh,
              }
            )}
          />
        </RadioGroup.Root>

        <div className="relative mt-16 rounded-md border border-green-1 p-8">
          <Line options={options(stepSize, t)} data={data} />
        </div>

        <div
          className={cx(
            'mt-16',
            'md:flex md:space-x-24',
            'lg:block lg:space-x-0',
            'mt-16 xl:flex xl:space-x-24'
          )}
        >
          <ul className="flex grow justify-center space-x-8 whitespace-nowrap text-12 font-semibold xl:justify-start">
            <li className="flex items-center">
              <div className="mr-4 w-14 border-t-2 border-teal-3" />{' '}
              {t('acreSaver')}
            </li>
            <li className="flex items-center">
              <div className="mr-4 w-14 border-t-2 border-tan-3" />{' '}
              {t('acreBoost')}
            </li>
            <li className="flex items-center">
              <div className="mr-4 w-14 border-t-2 border-dashed border-grey-6" />{' '}
              {t('mortgage')}
            </li>
          </ul>
          <p
            className={cx('mt-16 rounded-sm border p-8 text-12 xl:mt-0', {
              'border-green-1 bg-grey-2': hpa !== calculator.chartHpaDown,
              'border-error-1 bg-error-2': hpa === calculator.chartHpaDown,
            })}
          >
            {hpa === calculator.chartHpaDown
              ? t('chart.down-appreciation-description', {
                  hpa: Math.round(calculator.chartHpaDown * 10000) / 100,
                })
              : null}
            {hpa === calculator.chartHpaStandard
              ? t('chart.standard-appreciation-description', {
                  hpa: Math.round(calculator.chartHpaStandard * 10000) / 100,
                })
              : null}
            {hpa === calculator.chartHpaHigh
              ? t('chart.high-appreciation-description', {
                  hpa: Math.round(calculator.chartHpaHigh * 10000) / 100,
                })
              : null}
          </p>
        </div>
      </div>
    </div>
  )
}

export default Chart
